// ── HOME ─────────────────────────────────────────────────────────────────────
const { useMemo, useRef, useState, useLayoutEffect } = React;
const { Ico, LeafMark, Photo, Pic, Btn, Eyebrow, Stat, Reveal, STAY_ICONS } = window.BCKit;

// Render a string with *asterisk-wrapped* segments in the italic-amber accent.
// Lets admin-editable single-line titles keep the live site's accent treatment.
const renderAccent = (text) =>
  String(text == null ? "" : text).split(/(\*[^*]+\*)/g).map((seg, i) =>
    seg.length > 1 && seg[0] === "*" && seg[seg.length - 1] === "*"
      ? <span key={i} className="serif-accent">{seg.slice(1, -1)}</span>
      : <React.Fragment key={i}>{seg}</React.Fragment>
  );

// Two-line headline with line 2 set in the italic-amber accent (the live site's
// signature treatment). Guards null/undefined so a blank or malformed store value
// degrades to empty text instead of throwing and taking the whole page down.
const renderHeadline = (text) =>
  String(text == null ? "" : text).split("\n").map((line, i) => (
    <React.Fragment key={i}>{i > 0 && <br />}{i === 1 ? <span className="serif-accent">{line}</span> : line}</React.Fragment>
  ));

// About feature-grid icons: a stored icon KEY → glyph. LeafMark takes size/color;
// the Ico.* glyphs take a style prop — handle both. Keys here match the admin picker.
const FEATURE_ICON_KEYS = ["star", "leaf", "check", "cal", "pin", "gift", "info", "phone"];
const renderFeatureIcon = (key) => {
  if (key === "leaf") return <LeafMark size={18} color="#2f5e2a" />;
  const I = { star: Ico.star, check: Ico.check, cal: Ico.cal, pin: Ico.pin, gift: Ico.gift, info: Ico.info, phone: Ico.phone }[key] || Ico.star;
  return <I style={{ width: 18, height: 18 }} />;
};

// Fixed bits of each "Pick your spot" card (route + look). The editable bits
// (title/description/photo) live per page at data.pages.<id>.card; the price is
// derived from that page's pricing via stayPrices().
const STAYS_META = [
  { id: "rv", tone: "pine", tag: "30 & 50 amp", popular: false, ph: "RV sites at dusk" },
  { id: "tent", tone: "meadow", tag: "Pine canopy", popular: false, ph: "Tent under pines" },
  { id: "cabins", tone: "dusk", tag: "A/C + heat", popular: true, ph: "Cabin porch glow" },
];

// Distinct, ascending nightly prices for a stay type, read from its page pricing.
const stayPrices = (data, id) => {
  const p = (data.pages && data.pages[id]) || {};
  let nums = [];
  if (id === "rv") nums = ((p.pricing && p.pricing.tiers) || []).map(t => Number(t.price));
  else if (id === "tent") nums = [Number(p.intro && p.intro.price)];
  else if (id === "cabins") nums = (p.units || []).map(u => Number(u.price));
  return [...new Set(nums.filter(n => Number.isFinite(n) && n > 0))].sort((a, b) => a - b);
};

const HomeHero = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const { hero } = data;
  // Amber stat row — composed from store data (about stats + season window) so it
  // stays admin-editable, matching the live hero's stat strip.
  const stats = (data.home.about.stats || []).slice(0, 3);
  const seasonChip = data.season && data.season.chip;
  return (
  <section style={{ position: "relative", height: "100vh", minHeight: 660, overflow: "hidden" }}>
    <Photo tone="dusk" src={hero.photo} h="100%" eager sizes="100vw" label="Hero · campground at golden hour" style={{ position: "absolute", inset: 0 }} />
    <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,rgba(15,22,12,.42) 0%,rgba(15,22,12,.12) 34%,rgba(15,22,12,.78) 100%)" }} />
    <div className="wrap" style={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: 80 }}>
      {/* `now`: the server already painted and faded this block in via
          .reveal--auto, so re-animating here would fade it a second time. */}
      <Reveal now>
        {hero.badge && (
          <span className="chip chip--static" style={{ background: "rgba(22,48,26,.5)", borderColor: "rgba(255,255,255,.3)", color: "#fff", backdropFilter: "blur(6px)" }}>
            <LeafMark size={14} color="#fff" /> {hero.badge}
          </span>
        )}
        <h1 className="display display--hero" style={{ fontSize: "clamp(52px,9vw,96px)", color: "#fff", marginTop: 18, textShadow: "0 4px 30px rgba(0,0,0,.4)", lineHeight: .95 }}>
          {renderHeadline(hero.headline)}
        </h1>
        <p style={{ fontSize: 19, color: "rgba(255,255,255,.9)", maxWidth: 480, marginTop: 18, lineHeight: 1.55, textShadow: "0 2px 12px rgba(0,0,0,.4)" }}>
          {hero.sub}
        </p>
        <div style={{ display: "flex", gap: 14, marginTop: 30, flexWrap: "wrap" }}>
          <Btn kind="primary" size="lg" icon={<Ico.phone />} {...window.BCStore.resolveCtaProps(hero.ctaPrimaryLink, { data, go }, { action: "phone", value: "" })}>{hero.ctaPrimary || "Book by phone"}</Btn>
          <Btn kind="white" size="lg" iconRight={<Ico.arrow />} {...window.BCStore.resolveCtaProps(hero.ctaLink, { data, go }, { action: "page", value: "rv" })}>{hero.cta}</Btn>
        </div>
        {(stats.length > 0 || seasonChip) && (
          <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 40, flexWrap: "wrap" }}>
            {stats.map(([n, l], i) => (
              <React.Fragment key={i}>
                {i > 0 && <span style={{ width: 1, height: 32, background: "rgba(255,255,255,.25)" }} />}
                <div>
                  <div className="serif" style={{ fontSize: 27, fontWeight: 700, color: "var(--amber)", lineHeight: 1 }}>{n}</div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.72)", marginTop: 6 }}>{l}</div>
                </div>
              </React.Fragment>
            ))}
            {seasonChip && (
              <>
                <span style={{ width: 1, height: 32, background: "rgba(255,255,255,.25)" }} />
                <div>
                  <div className="serif" style={{ fontSize: 27, fontWeight: 700, color: "var(--amber)", lineHeight: 1 }}>Open</div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.72)", marginTop: 6 }}>{seasonChip}</div>
                </div>
              </>
            )}
          </div>
        )}
      </Reveal>
    </div>
    <div className="scroll-cue" onClick={() => window.scrollTo({ top: window.innerHeight - 70, behavior: "smooth" })} title="Scroll to explore">
      <Ico.chev style={{ width: 30, height: 30 }} />
    </div>
  </section>
  );
};

// Replaces the old dead 4-field mock booking widget with a single working
// "call to check availability" strip (the live site has no fake booking form).
const BookingBar = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const bb = data.home.bookingBar || {};
  return (
    <div className="wrap" style={{ marginTop: 32, position: "relative", zIndex: 20 }}>
      <Reveal>
        <div className="availbar">
          <div>
            <div className="serif" style={{ fontSize: 23, fontWeight: 700 }}>{bb.title}</div>
            <p style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 5, fontStyle: "italic", maxWidth: 460 }}>
              {bb.sub}
            </p>
          </div>
          <Btn kind="amber" size="lg" icon={<Ico.phone />} {...window.BCStore.resolveCtaProps(bb.buttonLink, { data, go }, { action: "phone", value: "" })}>{bb.button || "Call to check availability"}</Btn>
        </div>
      </Reveal>
    </div>
  );
};

const AboutBlock = () => {
  const [data] = window.BCStore.useStore();
  const about = data.home.about;
  const features = about.features || [];
  const badge = about.badge || {};
  return (
  <section className="section">
    <div className="wrap">
      <div className="grid2" style={{ alignItems: "center", "--g2": "1fr 1.15fr" }}>
        <Reveal>
          <Eyebrow>{about.eyebrow}</Eyebrow>
          <h2 className="display" style={{ fontSize: "clamp(34px,4.4vw,46px)", marginTop: 12 }}>
            {renderHeadline(about.title)}
          </h2>
          <p className="lede" style={{ marginTop: 18, maxWidth: 460 }}>{about.body}</p>
          <div style={{ display: "flex", gap: 44, marginTop: 28 }}>
            {(about.stats || []).map(([n, l], i) => <Stat key={i} n={n} l={l} />)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 30, maxWidth: 470 }}>
            {features.map((f, i) => (
              <div key={f.id || i} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <span style={{ width: 38, height: 38, borderRadius: 10, background: "var(--green-soft)", color: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{renderFeatureIcon(f.icon)}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14.5 }}>{f.title}</div>
                  {f.sub && <div style={{ color: "var(--muted)", fontSize: 12.5 }}>{f.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div style={{ position: "relative" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gridTemplateRows: "1fr 1fr", gap: 14, height: 420 }}>
              {(about.photos || []).map((p, i) => (
                <Photo key={i} tone={p.tone} src={p.src} label={p.label} r={18} sizes={i === 0 ? "(max-width:880px) 54vw, 360px" : "(max-width:880px) 36vw, 240px"} style={i === 0 ? { gridRow: "1 / 3" } : undefined} />
              ))}
            </div>
            <div className="card" style={{ position: "absolute", left: -16, bottom: -18, padding: "13px 17px", display: "flex", alignItems: "center", gap: 12, boxShadow: "var(--shadow-md)" }}>
              <span style={{ width: 40, height: 40, borderRadius: 11, background: "var(--amber)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Ico.star style={{ width: 20, height: 20 }} />
              </span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{[badge.value, badge.label].filter(Boolean).join(" ")}</div>
                <div style={{ color: "var(--muted)", fontSize: 12 }}>{badge.sub}</div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

const StaysGrid = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const stays = data.home.stays || {};
  return (
  <section className="section" style={{ background: "var(--paper-2)" }}>
    <div className="wrap">
      <Reveal>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 36 }}>
          <div>
            <Eyebrow>{stays.eyebrow}</Eyebrow>
            <h2 className="display" style={{ fontSize: "clamp(32px,4vw,44px)", marginTop: 10 }}>{renderAccent(stays.title)}</h2>
          </div>
          <p style={{ color: "var(--muted)", maxWidth: 320, fontSize: 15, lineHeight: 1.6 }}>{stays.sub}</p>
        </div>
      </Reveal>
      <div className="grid3">
        {STAYS_META.map((s, i) => {
          const card = ((data.pages[s.id] && data.pages[s.id].card) || {});
          const prices = stayPrices(data, s.id);
          const G = STAY_ICONS[s.id];
          return (
          <Reveal delay={i * 90} key={s.id}>
            <div className="card card--hover" style={{ cursor: "pointer", height: "100%" }} onClick={() => go(s.id)}>
              <div style={{ position: "relative" }}>
                <Photo tone={s.tone} src={card.src} label={card.t || s.ph} h={210} sizes="(max-width:560px) 92vw, (max-width:880px) 45vw, 33vw" />
                {s.popular && <span className="chip" style={{ position: "absolute", top: 14, left: 14, background: "var(--amber)", borderColor: "var(--amber)", color: "#fff", fontSize: 11.5, fontWeight: 600 }}>★ Most booked</span>}
                <span className="chip chip--static" style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,.92)", borderColor: "transparent", fontSize: 11.5 }}>{s.tag}</span>
              </div>
              <div style={{ padding: "22px 22px 24px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                  <h3 className="serif" style={{ fontSize: 25, fontWeight: 700, display: "flex", alignItems: "center", gap: 9 }}>{G ? <G style={{ width: 18, height: 18, color: "#2f5e2a", flexShrink: 0 }} /> : null}{card.t}</h3>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {prices.length <= 1 ? (
                      <>
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>from </span>
                        <span className="serif" style={{ fontSize: 24, fontWeight: 700, color: "var(--green)" }}>${prices[0] || 0}</span>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>from </span>
                        <span className="serif" style={{ fontSize: 20, fontWeight: 700, color: "var(--green)", whiteSpace: "nowrap" }}>
                          ${prices[0]} – ${prices[prices.length - 1]}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <p style={{ color: "var(--muted)", fontSize: 14.5, marginTop: 6 }}>{card.sub}</p>
                <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 8, color: "var(--green)", fontWeight: 600, fontSize: 14.5 }}>
                  View all photos <Ico.arrow style={{ width: 16, height: 16 }} />
                </div>
              </div>
            </div>
          </Reveal>
          );
        })}
      </div>
    </div>
  </section>
  );
};

// Fisher–Yates shuffle (returns the same array, shuffled in place)
const shuffleArr = (a) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = a[i]; a[i] = a[j]; a[j] = t;
  }
  return a;
};
const GalleryStrip = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const photos = data.photos || [];
  const source = (data.grounds && data.grounds.source) || "auto";

  // Which photos feed the strip. "selected" uses only featured photos, but
  // falls back to the full set when nothing is featured (never an empty band).
  const pool = useMemo(() => {
    const featured = photos.filter((p) => p.featured);
    const base = source === "selected" && featured.length ? featured : photos;
    return source === "auto" ? shuffleArr(base.slice()) : base.slice();
  }, [source, photos]);

  // One horizontal row, duplicated ×2 so translateX(-50%) loops seamlessly.
  // Force an even single-copy length so the higher/lower zig-zag (CSS nth-child)
  // stays aligned across the loop seam.
  const tiles = useMemo(() => {
    if (!pool.length) return [];
    let seq = pool;
    while (seq.length < 12) seq = seq.concat(pool);
    if (seq.length % 2) seq = seq.concat(pool.slice(0, 1));
    return seq.concat(seq);
  }, [pool]);

  // Constant scroll speed regardless of photo count OR shape. Cards are now
  // variable-width (each photo keeps its natural aspect ratio), so a per-tile
  // estimate would drift — instead measure the real track width and set the
  // duration to (half the track) / SPEED. The track is seq.concat(seq) animating
  // translateX(-50%), so one loop traverses exactly half its width.
  const SPEED = 79; // px per second — matches the prior feel
  const trackRef = useRef(null);
  const [dur, setDur] = useState(null);
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.scrollWidth;
      if (w) setDur((w / 2 / SPEED) + "s");
    };
    measure(); // images may still be loading — ResizeObserver catches the rest
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tiles]);

  if (!tiles.length) return null; // no photos at all → hide the section

  return (
    <section className="section--tight" style={{ borderTop: "1px solid var(--rule)", borderBottom: "1px solid var(--rule)", background: "var(--white)" }}>
      <div className="wrap" style={{ marginBottom: 22 }}>
        <Reveal>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <h3 className="serif" style={{ fontSize: 26, fontWeight: 700 }}>{renderAccent((data.home.gallery && data.home.gallery.title) || "")}</h3>
            <span onClick={() => go("gallery")} style={{ color: "var(--green)", fontWeight: 600, fontSize: 14.5, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }}>
              All {photos.length} photos <Ico.arrow style={{ width: 16, height: 16 }} />
            </span>
          </div>
        </Reveal>
      </div>
      <Reveal delay={80}>
        <div className="gstrip">
          {/* --dur is measured from the real track width (see effect above) so
              the speed stays constant no matter how many photos — or what shapes. */}
          <div ref={trackRef} className="gstrip__track" style={dur ? { "--dur": dur } : undefined}>
            {tiles.map((p, k) => (
              <div key={k} className="gstrip__ph" onClick={() => go("gallery")}>
                {p.src
                  ? <Pic className="gstrip__img" src={p.src} sizes="(max-width:880px) 410px, 620px" />
                  : <Photo tone={p.tone} r={16} style={{ width: 220, height: "100%" }} />}
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};

// Multi-colour Google "G" glyph for the review cards.
const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>
    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
  </svg>
);

// Google-style review cards — content lives in the store (home.reviews) so the
// admin can add/remove reviews and edit the rating header + pull-quote.
const Reviews = () => {
  const [data] = window.BCStore.useStore();
  const rv = data.home.reviews || {};
  const items = rv.items || [];
  const pq = rv.pullQuote || {};
  return (
  <section className="section">
    <div className="wrap">
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <Eyebrow>{rv.eyebrow}</Eyebrow>
          <h2 className="display" style={{ fontSize: "clamp(32px,4vw,44px)", marginTop: 10 }}>{renderAccent(rv.title)}</h2>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginTop: 16, flexWrap: "wrap", justifyContent: "center" }}>
            <span style={{ display: "flex", gap: 3, color: "var(--amber)" }}>{[0,1,2,3,4].map(s => <Ico.star key={s} style={{ width: 18, height: 18 }} />)}</span>
            <span style={{ fontWeight: 700, fontSize: 15.5 }}>{rv.ratingValue}</span>
            <span style={{ color: "var(--muted)", fontSize: 14.5 }}>· Based on {rv.ratingCount} Google reviews</span>
          </div>
        </div>
      </Reveal>
      <div className="masonry masonry--rv">
        {items.map((r, i) => {
          // A review with a link renders as a real <a> to that exact Google
          // review (new tab); without one it stays a plain card.
          const Card = r.url ? "a" : "div";
          const linkProps = r.url ? { href: r.url, target: "_blank", rel: "noreferrer", "aria-label": `Read ${r.name}'s review on Google` } : {};
          return (
          <Reveal delay={i * 90} key={r.id || i}>
            <Card className={`card ${r.url ? "card--hover" : ""}`} {...linkProps} style={{ padding: "26px 26px", display: "block", textDecoration: "none", color: "inherit", cursor: r.url ? "pointer" : "default" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <span style={{ width: 42, height: 42, borderRadius: "50%", background: "var(--green-soft)", color: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 17, flexShrink: 0 }}>{(r.name || "").replace(/^The\s+/, "").charAt(0)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14.5 }}>{r.name}</div>
                  <div style={{ color: "var(--muted)", fontSize: 12.5 }}>{r.meta}</div>
                </div>
                <GoogleG />
              </div>
              <div style={{ display: "flex", gap: 3, color: "var(--amber)", marginBottom: 12 }}>
                {[0,1,2,3,4].map(s => <Ico.star key={s} style={{ width: 15, height: 15 }} />)}
              </div>
              <p className="serif" style={{ fontSize: 17.5, fontWeight: 500, lineHeight: 1.5, fontStyle: "italic" }}>"{r.quote}"</p>
            </Card>
          </Reveal>
          );
        })}
      </div>
      {pq.text && (
        <Reveal delay={120}>
          <div style={{ marginTop: 24, background: "var(--green-deep)", color: "#fff", borderRadius: 20, padding: "46px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -80, right: -50, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle,rgba(194,132,29,.28),transparent 68%)" }} />
            <p className="serif" style={{ fontSize: "clamp(22px,3vw,30px)", fontWeight: 500, fontStyle: "italic", lineHeight: 1.4, maxWidth: 760, margin: "0 auto", position: "relative" }}>
              "{pq.text}"
            </p>
            {pq.attribution && <div style={{ marginTop: 18, fontSize: 14, color: "rgba(255,255,255,.6)", position: "relative" }}>{pq.attribution}</div>}
          </div>
        </Reveal>
      )}
    </div>
  </section>
  );
};

const LocationCTA = () => {
  const [data] = window.BCStore.useStore();
  const { contact, officeHours } = data;
  const loc = data.home.location || {};
  return (
  <section className="section--tight" style={{ paddingBottom: 90 }}>
    <div className="wrap">
      <div className="grid2" style={{ "--g2": "1.4fr 1fr", alignItems: "stretch" }}>
        <Reveal>
          <div style={{ height: "100%" }}>
            <Eyebrow>{loc.eyebrow}</Eyebrow>
            <h2 className="display" style={{ fontSize: "clamp(28px,3.4vw,38px)", marginTop: 10, marginBottom: 18 }}>{loc.title}</h2>
            <MapBox />
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div style={{ background: "var(--green)", color: "#fff", borderRadius: 20, padding: "34px 32px", height: "100%", display: "flex", flexDirection: "column" }}>
            <Eyebrow style={{ color: "rgba(255,255,255,.55)" }}>{loc.ctaEyebrow}</Eyebrow>
            <h3 className="serif" style={{ fontSize: 30, fontWeight: 700, marginTop: 10 }}>{loc.ctaTitle}</h3>
            <a href={`tel:${window.BCStore.telHref(contact.phone)}`} className="serif" style={{ fontSize: 32, fontWeight: 700, marginTop: 18, display: "block", color: "#fff" }}>{contact.phone}</a>
            <a href={`mailto:${contact.email}`} style={{ color: "rgba(255,255,255,.78)", fontSize: 14.5, marginTop: 8, display: "block" }}>{contact.email}</a>
            <div style={{ marginTop: "auto", paddingTop: 24, borderTop: "1px solid rgba(255,255,255,.2)" }}>
              <Eyebrow style={{ color: "rgba(255,255,255,.55)", marginBottom: 12 }}>Office hours</Eyebrow>
              {officeHours.map(([d,t], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 14.5, padding: "5px 0", color: "rgba(255,255,255,.9)" }}>
                  <span>{d}</span><span style={{ color: "rgba(255,255,255,.7)" }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

// Real Google Map embed, keyed off the store address (no API key required), so
// it tracks whatever the admin sets for data.contact.addr1 / addr2.
const MapBox = () => {
  const [data] = window.BCStore.useStore();
  const { addr1, addr2 } = data.contact;
  const query = [addr1, addr2].filter(Boolean).join(", ") || "Butterfly Camping Resort, New Jersey";
  const q = encodeURIComponent(query);
  return (
    <div style={{ position: "relative", height: 340, borderRadius: 18, overflow: "hidden", boxShadow: "var(--shadow-sm)", border: "1px solid var(--rule)" }}>
      <iframe title="Find Butterfly Camping Resort" loading="lazy" allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        src={`https://www.google.com/maps?q=${q}&output=embed`}
        style={{ width: "100%", height: "100%", border: 0, display: "block" }} />
    </div>
  );
};

const PinnedJournal = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const published = (data.blog || []).filter((p) => p.status === "published");
  if (!published.length) return null;
  const pinned = published.filter((p) => p.pinned);
  const list = (pinned.length ? pinned : published).slice(0, 3); // fall back to latest 3
  const journal = data.home.journal || {};
  return (
    <section className="section">
      <div className="wrap">
        <Reveal>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 28 }}>
            <div>
              <Eyebrow>{journal.eyebrow}</Eyebrow>
              <h2 className="display" style={{ fontSize: "clamp(28px,3.4vw,40px)", marginTop: 6 }}>{journal.title}</h2>
            </div>
            <span onClick={() => go("blog")} style={{ color: "var(--green)", fontWeight: 600, fontSize: 14.5, cursor: "pointer", display: "flex", gap: 6, alignItems: "center" }}>
              All posts <Ico.arrow style={{ width: 16, height: 16 }} />
            </span>
          </div>
        </Reveal>
        <div className="grid3">
          {list.map((p, i) => {
            const postId = "blog/" + window.BCStore.postSlug(p);
            return (
            <Reveal delay={i * 90} key={p.id}>
              <a className="card card--hover" href={window.BCStore.idToPath(postId)} style={{ cursor: "pointer", height: "100%", display: "block", color: "inherit", textDecoration: "none" }}
                onClick={(e) => {
                  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  go(postId);
                }}>
                <Photo tone={p.tone || "meadow"} src={p.src} label={p.title} h={180} sizes="(max-width:560px) 92vw, (max-width:880px) 45vw, 33vw" />
                <div style={{ padding: "20px 22px 24px" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--green)" }}>{p.cat}</span>
                  <h3 className="serif" style={{ fontSize: 21, fontWeight: 700, marginTop: 8, lineHeight: 1.2 }}>{p.title}</h3>
                  <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>{p.excerpt}</p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16 }}>
                    <span style={{ fontSize: 12, color: "var(--muted)" }}>{p.date}{p.readMins ? ` · ${p.readMins} min` : ""}</span>
                    <span style={{ color: "var(--green)", fontWeight: 600, fontSize: 14, display: "flex", gap: 6, alignItems: "center" }}>Read <Ico.arrow style={{ width: 15, height: 15 }} /></span>
                  </div>
                </div>
              </a>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── AMENITIES ("Everything You Need Outdoors") ───────────────────────────────
// Store-driven: reuses data.pages.rv.amenities (eyebrow/title/items). Only the
// decorative photo + badge label are design.
const Amenities = () => {
  const [data] = window.BCStore.useStore();
  const am = (data.pages && data.pages.rv && data.pages.rv.amenities) || {};
  const amBadge = (data.home.amenities && data.home.amenities.badge) || {};
  const amPhoto = (data.home.amenities && data.home.amenities.photo) || "";
  const items = am.items || [];
  if (!items.length) return null;
  return (
  <section className="section" style={{ background: "var(--paper-2)" }}>
    <div className="wrap">
      <div className="grid2 grid2--rev" style={{ alignItems: "center", "--g2": "1fr 1.05fr" }}>
        <Reveal>
          <div style={{ position: "relative" }}>
            <Photo tone="water" src={amPhoto} label="Pool & facilities" h={420} r={20} sizes="(max-width:880px) 92vw, 50vw" />
            <div className="card" style={{ position: "absolute", right: -16, bottom: -18, padding: "13px 17px", display: "flex", alignItems: "center", gap: 12, boxShadow: "var(--shadow-md)" }}>
              <span style={{ width: 40, height: 40, borderRadius: 11, background: "var(--green)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Ico.check style={{ width: 20, height: 20 }} />
              </span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{amBadge.title}</div>
                <div style={{ color: "var(--muted)", fontSize: 12 }}>{amBadge.sub}</div>
              </div>
            </div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <Eyebrow>{am.eyebrow}</Eyebrow>
          <h2 className="display" style={{ fontSize: "clamp(30px,4vw,44px)", marginTop: 10, marginBottom: 24 }}>{am.title}</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {items.map((a) => (
              <div key={a} className="card" style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 11 }}>
                <span style={{ width: 32, height: 32, borderRadius: 9, background: "var(--amber-soft)", color: "var(--green)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Ico.check style={{ width: 15, height: 15 }} />
                </span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{a}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};

// ── ACTIVITIES PREVIEW ("Fun for the Whole Family") ──────────────────────────
// Store-driven cards from data.pages.activities.items over a darkened collage bg.
const ACT_ICONS = [Ico.star, Ico.gift, Ico.cal, Ico.pin, Ico.check, Ico.info];
const ActivitiesPreview = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const a = (data.pages && data.pages.activities) || {};
  const items = (a.items || []).slice(0, 6);
  if (!items.length) return null;
  const hero = a.hero || {};
  const ha = data.home.activities || {};
  return (
  <section style={{ position: "relative", background: "var(--green-deep)", color: "#fff", overflow: "hidden" }}>
    <div style={{ position: "absolute", inset: 0, opacity: .5,
      backgroundImage: "linear-gradient(135deg,#1f3a1f,transparent 60%),linear-gradient(315deg,#3a6068,transparent 55%),linear-gradient(45deg,#9a5a2c,transparent 50%)" }} />
    <div style={{ position: "absolute", inset: 0, background: "rgba(15,28,18,.6)" }} />
    <div className="wrap section" style={{ position: "relative" }}>
      <Reveal>
        <div style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 44px" }}>
          {hero.eyebrow && <Eyebrow style={{ color: "rgba(255,255,255,.55)" }}>{hero.eyebrow}</Eyebrow>}
          <h2 className="display" style={{ fontSize: "clamp(32px,4.4vw,48px)", color: "#fff", marginTop: 12 }}>
            {renderAccent(ha.heading)}
          </h2>
        </div>
      </Reveal>
      <div className="grid3">
        {items.map((it, i) => {
          const I = ACT_ICONS[i % ACT_ICONS.length];
          return (
          <Reveal delay={(i % 3) * 90} key={it.id}>
            <div style={{ background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.13)", borderRadius: 16, padding: "26px 26px", height: "100%", backdropFilter: "blur(4px)" }}>
              <span style={{ width: 46, height: 46, borderRadius: 12, background: "var(--amber)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <I style={{ width: 22, height: 22 }} />
              </span>
              <h3 className="serif" style={{ fontSize: 22, fontWeight: 700, marginTop: 16, color: "#fff" }}>{it.title}</h3>
              <p style={{ color: "rgba(255,255,255,.72)", fontSize: 14.5, marginTop: 8, lineHeight: 1.6 }}>{it.desc}</p>
              {(it.tags && it.tags.length > 0) && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 14 }}>
                  {it.tags.slice(0, 4).map((t, k) => (
                    <span key={k} style={{ fontSize: 11.5, fontWeight: 600, color: "rgba(255,255,255,.85)", background: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.16)", borderRadius: 999, padding: "3px 10px" }}>{t}</span>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
          );
        })}
      </div>
      <Reveal delay={120}>
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <Btn kind="amber" size="lg" iconRight={<Ico.arrow />} {...window.BCStore.resolveCtaProps(ha.buttonLink, { data, go }, { action: "page", value: "activities" })}>{ha.button || "See all activities"}</Btn>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

// ── EXPLORE NEARBY ───────────────────────────────────────────────────────────
// Store-driven (home.nearby): heading + cards are admin add/removable.
const ExploreNearby = () => {
  const [data] = window.BCStore.useStore();
  const nb = data.home.nearby || {};
  const items = nb.items || [];
  if (!items.length) return null;
  return (
  <section className="section">
    <div className="wrap">
      <div className="grid2" style={{ "--g2": "1fr 1.2fr", alignItems: "center" }}>
        <Reveal>
          <Eyebrow>{nb.eyebrow}</Eyebrow>
          <h2 className="display" style={{ fontSize: "clamp(30px,4vw,44px)", marginTop: 10 }}>
            {renderAccent(nb.title)}
          </h2>
          <p className="lede" style={{ marginTop: 16, maxWidth: 380 }}>
            {nb.sub}
          </p>
        </Reveal>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {items.map((n, i) => (
            <Reveal delay={i * 90} key={n.id || i}>
              <div className="card card--hover" style={{ padding: "22px 24px", display: "flex", gap: 18, alignItems: "flex-start" }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--amber)", whiteSpace: "nowrap", paddingTop: 3, minWidth: 92 }}>{n.distance}</span>
                <div>
                  <h3 className="serif" style={{ fontSize: 20, fontWeight: 700 }}>{n.title}</h3>
                  <p style={{ color: "var(--muted)", fontSize: 14.5, marginTop: 5, lineHeight: 1.6 }}>{n.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

// ── FINAL CTA ("Ready to Escape?") ───────────────────────────────────────────
// Deep-green band; headline/sub/labels from the store (home.finalCta), phone from contact.
const FinalCTA = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const { contact } = data;
  const fc = data.home.finalCta || {};
  return (
  <section style={{ background: "var(--green-deep)", color: "#fff", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: -120, right: -80, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle,rgba(194,132,29,.3),transparent 68%)" }} />
    <div style={{ position: "absolute", bottom: -160, left: -120, width: 420, height: 420, borderRadius: "50%", background: "radial-gradient(circle,rgba(47,94,42,.7),transparent 70%)" }} />
    <div className="wrap section" style={{ position: "relative" }}>
      <Reveal>
        <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto" }}>
          <Eyebrow style={{ color: "rgba(255,255,255,.55)" }}>{fc.eyebrow}</Eyebrow>
          <h2 className="display" style={{ fontSize: "clamp(36px,5vw,58px)", color: "#fff", marginTop: 14 }}>
            {renderAccent(fc.title)}
          </h2>
          <p style={{ fontSize: 17.5, color: "rgba(255,255,255,.78)", marginTop: 16, lineHeight: 1.6 }}>
            {fc.sub}
          </p>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 28, flexWrap: "wrap" }}>
            <Btn kind="amber" size="lg" icon={<Ico.phone />} {...window.BCStore.resolveCtaProps(fc.primaryLink, { data, go }, { action: "phone", value: "" })}>Call {contact.phone}</Btn>
            <Btn kind="white" size="lg" iconRight={<Ico.arrow />} {...window.BCStore.resolveCtaProps(fc.secondaryLink, { data, go }, { action: "page", value: "rv" })}>{fc.secondaryLabel || "View accommodations"}</Btn>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};

const Home = ({ go }) => (
  <main>
    <HomeHero go={go} />
    <BookingBar go={go} />
    <AboutBlock />
    <StaysGrid go={go} />
    <Amenities />
    <GalleryStrip go={go} />
    <ActivitiesPreview go={go} />
    <PinnedJournal go={go} />
    <Reviews />
    <ExploreNearby />
    <LocationCTA />
    <FinalCTA go={go} />
  </main>
);

window.BCHome = { Home };
